'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Database, RefreshCw, Zap } from 'lucide-react';

interface WelcomeScreen {
  heading: string;
  icon: typeof Database;
  body: React.ReactNode;
}

const SCREENS: WelcomeScreen[] = [
  {
    heading: 'Your AMS, Connected',
    icon: Database,
    body: (
      <>
        <p className="text-base">
          Connecting your Agency Management System to Voley unlocks a powerful two-way
          link between your client data and your messaging. <strong>Client information
          automatically syncs</strong> so your team always has the latest details at
          their fingertips.
        </p>
        <p className="text-[15px]">
          No more switching between tabs or manually copying contacts. When a client
          record changes in your AMS, Voley picks it up automatically.
        </p>
      </>
    ),
  },
  {
    heading: 'What You Can Do',
    icon: RefreshCw,
    body: (
      <>
        <p className="text-base">
          With your AMS connected, Voley becomes the messaging layer built on top of
          your existing workflow:
        </p>
        <div className="flex flex-col gap-2.5 pl-2">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]"><strong>Contact syncing</strong> &mdash; new clients and updates flow from your AMS into Voley automatically</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]"><strong>Automatic logging</strong> &mdash; every conversation is documented in your AMS with timestamps and context</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]"><strong>Native messaging</strong> &mdash; use Voley directly within your AMS workflow so nothing falls outside your system of record</span>
          </div>
        </div>
        <p className="text-[15px] mt-3">
          This helps ensure <strong>compliance</strong>, saves your team significant time,
          and connects your tools across systems.
        </p>
      </>
    ),
  },
  {
    heading: "Let\u2019s Connect",
    icon: Zap,
    body: (
      <>
        <p className="text-base">
          We&rsquo;ll walk you through the setup:
        </p>
        <div className="flex flex-col gap-3 pl-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]">Click Connect on the HawkSoft card</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]">Enter your Agency ID from HawkSoft</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]">Enable automatic contact syncing</span>
          </div>
        </div>
        <p className="text-[15px] mt-4">
          The whole process takes less than a minute.
        </p>
      </>
    ),
  },
];

interface AmsConnectWelcomeModalProps {
  onComplete: () => void;
}

export function AmsConnectWelcomeModal({ onComplete }: AmsConnectWelcomeModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onComplete();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onComplete]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="AMS Connection Tour Welcome"
        >
          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{screen.heading}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Start the tour' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? "Let\u2019s Go" : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
